// Layer 2 (Engine): rule-set lifecycle — claims + rulings → live rules (construct.R8).
// See docs/engines/rule-set.md.

import type { Claim, ResolvedVersion, Ruling, VersionDiff } from '@/versions/v1/schema/claim';
import type { Case, FailureMode, FMEAUpdateReview } from '@/versions/v1/schema/case';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';
import type { AffectsFlag } from '@/versions/v1/schema/met-diagnostics-cause';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { applyFmeaReviews } from '@/versions/v1/shared/AdvisoryEngine';
import {
  claimsOfVersion,
  diffVersions,
} from '@/versions/v1/shared/claims';
import { productionDayOf, isoDateFromRaisedAt } from '@/versions/v1/shared/clock.derive';
import {
  FAILURE_MODE_RULE_IDS,
  RULE_CLAIMS,
  RULE_SET_VERSIONS,
  SEEDED_RULINGS,
} from '@/versions/v1/shared/bunker-hill-rule-claims';
import {
  CASE_OUTCOMES,
  FAILURE_MODES,
  FMEA_UPDATE_REVIEWS,
  VARIATION_EVENTS,
} from '@/versions/v1/shared/bunker-hill-advisory-seed';
import type { Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import { formatBand, liveControlPlanRows, bandForSection } from '@/versions/v1/shared/control-plan-band.derive';
import { oreZoneAt } from '@/versions/v1/shared/bunker-hill-ore-zones';
import type { ControlPlanSectionId } from '@/versions/v1/shared/bunker-hill-control-plan-seed';

export type LiveRuleSet = {
  version: ResolvedVersion;
  rules: DiagnosticRule[];
  proposed: Claim<DiagnosticRule>[];
  retired: Claim<DiagnosticRule>[];
};

export type LikelihoodRead = {
  hits: number;
  windowDays: number;
  authored: FailureMode['o'] | undefined;
};

export type RuleSetCounts = {
  live: number;
  proposed: number;
};

function allRulings(advisory: AdvisorySessionState): Ruling[] {
  return [...SEEDED_RULINGS, ...advisory.rulings];
}

function isSessionRuling(ruling: Ruling): boolean {
  return ruling.id.startsWith('session-ruling-');
}

function claimIsLive(claim: Claim<DiagnosticRule>, rulings: readonly Ruling[]): boolean {
  if (claim.status === 'accepted') return true;
  if (claim.status === 'disputed') {
    return rulings.some((row) => row.stage === 'approval' && row.acceptsClaimId === claim.id);
  }
  return false;
}

function sessionApprovalRulings(advisory: AdvisorySessionState): Ruling[] {
  return advisory.rulings.filter(
    (row) => row.stage === 'approval' && isSessionRuling(row),
  );
}

/** One resolved version per session approval — each supersedes the previous id in the chain. */
function sessionRuleSetVersions(advisory: AdvisorySessionState): ResolvedVersion[] {
  const approvals = sessionApprovalRulings(advisory);
  if (approvals.length === 0) return [];
  const base = RULE_SET_VERSIONS[RULE_SET_VERSIONS.length - 1]!;
  const versions: ResolvedVersion[] = [];
  let claimIds = [...base.claimIds];
  let prevId = base.id;
  let n = base.n;
  for (const approval of approvals) {
    if (
      approval.acceptsClaimId &&
      !claimIds.includes(approval.acceptsClaimId) &&
      approval.subject.kind !== 'band'
    ) {
      claimIds = [...claimIds, approval.acceptsClaimId];
    }
    n += 1;
    const version: ResolvedVersion = {
      id: `rs-v${n}`,
      scope: 'rule-set',
      n,
      acceptedAt: isoDateFromRaisedAt(approval.decidedAt, DEFAULT_CURSOR),
      acceptedBy: approval.author,
      claimIds: [...claimIds],
      supersedes: prevId,
      note: `Session approval · ${approval.reason}`,
    };
    versions.push(version);
    prevId = version.id;
  }
  return versions;
}

function buildVersion(advisory: AdvisorySessionState): ResolvedVersion {
  const session = sessionRuleSetVersions(advisory);
  if (session.length === 0) {
    return RULE_SET_VERSIONS[RULE_SET_VERSIONS.length - 1]!;
  }
  return session[session.length - 1]!;
}

export function liveRuleSet(advisory: AdvisorySessionState): LiveRuleSet {
  const version = buildVersion(advisory);
  const rulings = allRulings(advisory);
  const versionClaims = claimsOfVersion(version, RULE_CLAIMS);

  const rules: DiagnosticRule[] = [];
  const proposed: Claim<DiagnosticRule>[] = [];
  const retired: Claim<DiagnosticRule>[] = [];

  for (const claim of versionClaims) {
    if (claim.kind === 'rule' && claimIsLive(claim as Claim<DiagnosticRule>, rulings)) {
      const rule = claim.value as DiagnosticRule;
      if (!rules.some((row) => row.id === rule.id)) {
        rules.push(rule);
      }
    }
  }

  const versionClaimIds = new Set(version.claimIds);
  for (const claim of RULE_CLAIMS) {
    if (claim.status === 'rejected' || claim.status === 'superseded') {
      if (versionClaimIds.has(claim.id)) {
        retired.push(claim as Claim<DiagnosticRule>);
      }
    }
  }

  const reviews = applyFmeaReviews(FMEA_UPDATE_REVIEWS, advisory);
  for (const review of reviews) {
    if (review.approvalStatus !== 'pending' || review.reviewStatus !== 'approved') continue;
    const ruleId = review.proposedChange.ruleId;
    if (!ruleId) continue;
    const claim = RULE_CLAIMS.find(
      (row) => row.subject.kind === 'rule' && row.subject.id === ruleId && row.status === 'disputed',
    );
    if (claim && !proposed.some((row) => row.id === claim.id)) {
      proposed.push(claim as Claim<DiagnosticRule>);
    }
  }

  return {
    version,
    rules,
    proposed,
    retired,
  };
}

export function ruleSetDiff(advisory: AdvisorySessionState): VersionDiff {
  const current = buildVersion(advisory);
  const allVersions = [...RULE_SET_VERSIONS, ...sessionRuleSetVersions(advisory)];
  const previous =
    allVersions.find((row) => row.id === current.supersedes) ??
    RULE_SET_VERSIONS[RULE_SET_VERSIONS.length - 1]!;
  return diffVersions(previous, current, RULE_CLAIMS);
}

export function formatHitCount(hits: number, windowDays: number): string {
  const noun = hits === 1 ? 'hit' : 'hits';
  return `${hits} ${noun} · ${windowDays} d`;
}

export type ApprovalTab = 'to-review' | 'approved' | 'rejected';

export function approvalTabForReview(review: FMEAUpdateReview): ApprovalTab {
  if (review.reviewStatus === 'rejected' || review.approvalStatus === 'rejected') {
    return 'rejected';
  }
  if (review.approvalStatus === 'approved') {
    return 'approved';
  }
  return 'to-review';
}

export function proposedFmeaChangeCount(advisory: AdvisorySessionState): number {
  const reviews = applyFmeaReviews(FMEA_UPDATE_REVIEWS, advisory);
  return reviews.filter(
    (review) =>
      review.proposedChange.kind !== 'band-change' &&
      approvalTabForReview(review) === 'to-review',
  ).length;
}

/** Pending band-change reviews — same queue as FMEA, separate lifecycle. */
export function zoneBandProposals(advisory: AdvisorySessionState): readonly FMEAUpdateReview[] {
  const cursor = DEFAULT_CURSOR;
  const zone = oreZoneAt(cursor);
  const rows = liveControlPlanRows(advisory.controlPlanSessionRows);
  const reviews = applyFmeaReviews(FMEA_UPDATE_REVIEWS, advisory);
  return reviews.filter((review) => {
    if (review.proposedChange.kind !== 'band-change') return false;
    if (review.reviewStatus === 'rejected' || review.approvalStatus === 'rejected') return false;
    if (review.approvalStatus === 'approved') return false;
    const bandChange = review.proposedChange.bandChange;
    if (!bandChange) return false;
    const baseRow = rows.find((row) => row.id === bandChange.controlPlanRowId);
    if (baseRow?.fixed) return false;
    if (!zone || zone.id === bandChange.fromZoneId) return false;
    const sibling = rows.find(
      (row) =>
        row.sectionId === baseRow?.sectionId &&
        row.zoneId === zone.id &&
        row.id !== bandChange.controlPlanRowId,
    );
    return !sibling;
  });
}

export function pendingBandChangeForSection(
  sectionId: ControlPlanSectionId,
  advisory: AdvisorySessionState,
): FMEAUpdateReview | undefined {
  const rows = liveControlPlanRows(advisory.controlPlanSessionRows);
  const sectionRow = rows.find((row) => row.sectionId === sectionId && row.zoneId);
  if (!sectionRow || sectionRow.fixed) return undefined;
  return zoneBandProposals(advisory).find(
    (review) => review.proposedChange.bandChange?.controlPlanRowId === sectionRow.id,
  );
}

export function ruleSetCounts(advisory: AdvisorySessionState): RuleSetCounts {
  const { rules } = liveRuleSet(advisory);
  return { live: rules.length, proposed: proposedFmeaChangeCount(advisory) };
}

function parseRaisedAt(raisedAt: string, cursor: string): number {
  return Date.parse(`${isoDateFromRaisedAt(raisedAt, cursor)}T12:00:00Z`);
}

function inWindow(raisedAt: string, cursor: string, windowDays: number): boolean {
  const cursorMs = Date.parse(`${productionDayOf(cursor).date}T12:00:00Z`);
  const raisedMs = parseRaisedAt(raisedAt, cursor);
  const windowMs = windowDays * 24 * 60 * 60 * 1000;
  return cursorMs - raisedMs <= windowMs && raisedMs <= cursorMs;
}

export function matchedRuleIdForCase(
  caseItem: Case,
  diagnosis?: Diagnosis,
): string | undefined {
  if (caseItem.variationEventId) {
    const seeded = VARIATION_EVENTS.find((row) => row.id === caseItem.variationEventId);
    const seededRule = seeded?.matchedRules[0]?.ruleId;
    if (seededRule) return seededRule;
    const computed = diagnosis?.variationEvents.find((row) => row.id === caseItem.variationEventId);
    return computed?.matchedRules[0]?.ruleId;
  }
  const outcome = CASE_OUTCOMES.find((row) => row.caseId === caseItem.id);
  if (outcome?.proposedRuleId) return outcome.proposedRuleId;
  return undefined;
}

export function likelihood(
  ruleId: string,
  cases: readonly Case[],
  state: SessionState,
  diagnosis?: Diagnosis,
  windowDays = 14,
): LikelihoodRead {
  const cursor = state.cursor ?? DEFAULT_CURSOR;
  const computedCases = diagnosis?.cases ?? [];

  const allCases = [...cases, ...computedCases];
  const seen = new Set<string>();
  let hits = 0;
  for (const caseItem of allCases) {
    if (caseItem.branch !== 'zero-touch') continue;
    const rule = matchedRuleIdForCase(caseItem, diagnosis);
    if (rule !== ruleId) continue;
    if (seen.has(caseItem.id)) continue;
    if (!inWindow(caseItem.raisedAt, cursor, windowDays)) continue;
    seen.add(caseItem.id);
    hits += 1;
  }

  const fmId = Object.entries(FAILURE_MODE_RULE_IDS).find(([, id]) => id === ruleId)?.[0];
  const fm = fmId ? FAILURE_MODES.find((row) => row.id === fmId) : undefined;

  return { hits, windowDays, authored: fm?.o };
}

/** Outcome tag derived from `affects[]` — #254; never a separate authored field. */
export type RuleOutcome = 'recovery' | 'grade' | 'both' | 'penalty';

const RECOVERY_AFFECTS = new Set<AffectsFlag>(['pb-recovery', 'zn-recovery', 'ag-recovery']);
const GRADE_AFFECTS = new Set<AffectsFlag>(['pb-con-grade', 'zn-con-grade']);

export function ruleOutcome(rule: DiagnosticRule): RuleOutcome | undefined {
  if (!rule.affects?.length) return undefined;
  const hasRecovery = rule.affects.some((flag) => RECOVERY_AFFECTS.has(flag));
  const hasGrade = rule.affects.some((flag) => GRADE_AFFECTS.has(flag));
  const hasPenalty = rule.affects.includes('penalty-elements');
  if (hasRecovery && hasGrade) return 'both';
  if (hasRecovery) return 'recovery';
  if (hasGrade) return 'grade';
  if (hasPenalty) return 'penalty';
  return undefined;
}

export const RULE_OUTCOME_LABEL: Record<RuleOutcome, string> = {
  recovery: 'recovery item',
  grade: 'grade item',
  both: 'recovery and grade',
  penalty: 'penalty item',
};

/** Assert every conflictsWith id resolves in the claim store (probe gate). */
export function assertClaimIntegrity(): string[] {
  const ids = new Set(RULE_CLAIMS.map((row) => row.id));
  const errors: string[] = [];
  for (const claim of RULE_CLAIMS) {
    for (const ref of claim.conflictsWith ?? []) {
      if (!ids.has(ref)) errors.push(`DANGLING conflictsWith: ${claim.id} → ${ref}`);
    }
    if (claim.subject.kind === 'band') {
      continue;
    }
    if (claim.kind === 'rule') {
      const rule = claim.value as DiagnosticRule;
      if (rule.sourceCauseId?.length && !rule.affects?.length) {
        errors.push(`MISSING affects: ${rule.id} has sourceCauseId but no affects[]`);
      }
    }
  }
  return errors;
}

export function bandFieldsForProbe(
  advisory: AdvisorySessionState,
  cursor: SeriesTime = DEFAULT_CURSOR,
): Record<string, unknown> {
  const rows = liveControlPlanRows(advisory.controlPlanSessionRows);
  const bandChange = pendingBandChangeForSection('Zn Recovery Below Target', advisory);
  const znResolved = bandForSection('Zn Recovery Below Target', cursor, rows);
  const equipBand = bandForSection('equipment', cursor, rows);
  const pendingBand = zoneBandProposals(advisory).length;
  const cursorZone = oreZoneAt(cursor);
  const znZone =
    znResolved?.zoneId && bandChange && cursorZone
      ? cursorZone.label.split(' · ')[0] ?? cursorZone.label
      : znResolved?.zoneLabel ?? null;
  return {
    znRecoveryBand: znResolved
      ? `${formatBand(znResolved.band, znResolved.unit)}${znZone ? ` · ${znZone}` : ''}`
      : null,
    equipmentBand: equipBand ? formatBandProbe(equipBand) : null,
    pendingBandChangeReviews: pendingBand,
  };
}

function formatBandProbe(resolved: ReturnType<typeof bandForSection>): string {
  if (!resolved) return '—';
  const zone = resolved.zoneLabel ? ` · ${resolved.zoneLabel}` : '';
  const fixed = resolved.fixed ? ' · fixed' : '';
  return `${formatBand(resolved.band, resolved.unit)}${zone}${fixed}`;
}
